import { useRole } from '@/lib/role';
import type { Ticket } from '@/lib/mock';
import { useNow } from '@/lib/useNow';
import { Users, BarChart3, Clock, AlertTriangle, Briefcase, Activity, Mail } from 'lucide-react';

export function ManagerDashboard({ tickets }: { tickets: Ticket[] }) {
  const { role, teamId } = useRole();
  const now = useNow(5000);
  
  // Filter tickets for the manager's purview
  // In a real app, this would filter by their specific department or team
  // For demo, we just show all tickets or team-specific tickets if teamId is set
  const purviewTickets = teamId && role === 'Team Lead' 
    ? tickets.filter(t => t.team === teamId) // simplified matching
    : tickets;
    
  const openTickets = purviewTickets.filter(t => t.state !== 'Closed' && t.state !== 'Resolved');
  const criticalTickets = openTickets.filter(t => t.priority === 'P1');
  const overdueTickets = openTickets.filter(t => t.slaDueAt && t.slaDueAt < now);
  const unassigned = openTickets.filter(t => !t.assignee);
  
  // Fake approvals pending
  const pendingApprovals = 3;
  const emailTickets = purviewTickets.filter(t => t.source === 'Email');

  return (
    <div className="flex h-full flex-col bg-background p-6 overflow-y-auto">
      <div className="mx-auto max-w-7xl w-full">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {role === 'Department Head' ? 'Department Overview' : 'Management Console'}
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3 text-muted-foreground mb-2">
              <Activity className="h-5 w-5" />
              <h3 className="font-medium text-sm">Open Volume</h3>
            </div>
            <p className="text-3xl font-semibold text-foreground">{openTickets.length}</p>
          </div>
          
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3 text-destructive mb-2">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="font-medium text-sm">P1 Critical</h3>
            </div>
            <p className="text-3xl font-semibold text-destructive">{criticalTickets.length}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3 text-orange-500 mb-2">
              <Clock className="h-5 w-5" />
              <h3 className="font-medium text-sm">SLA Overdue</h3>
            </div>
            <p className="text-3xl font-semibold text-orange-500">{overdueTickets.length}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3 text-muted-foreground mb-2">
              <Users className="h-5 w-5" />
              <h3 className="font-medium text-sm">Unassigned</h3>
            </div>
            <p className="text-3xl font-semibold text-foreground">{unassigned.length}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm bg-primary/5 border-primary/20">
            <div className="flex items-center gap-3 text-primary mb-2">
              <Briefcase className="h-5 w-5" />
              <h3 className="font-medium text-sm">Approvals Needed</h3>
            </div>
            <p className="text-3xl font-semibold text-primary">{pendingApprovals}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3 text-cyan-600 mb-2">
              <Mail className="h-5 w-5" />
              <h3 className="font-medium text-sm">Email Tickets</h3>
            </div>
            <p className="text-3xl font-semibold text-cyan-600">{emailTickets.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm h-[400px] flex flex-col">
            <h2 className="font-display text-lg font-medium mb-4">Workload Distribution</h2>
            <div className="flex-1 flex items-center justify-center text-muted-foreground bg-muted/30 rounded-lg">
              <div className="text-center">
                <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>Team analytics visualization goes here</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm h-[400px] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-lg font-medium">Attention Required</h2>
              <span className="text-xs text-muted-foreground">{criticalTickets.length + overdueTickets.length} Items</span>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 pr-2">
              {[...criticalTickets, ...overdueTickets].slice(0, 8).map((t, i) => (
                <div key={`${t.id}-${i}`} className="flex justify-between items-start p-3 border border-border rounded-lg bg-background">
                  <div>
                    <div className="font-medium text-sm truncate max-w-[300px]">{t.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">{t.ref} • {t.assignee || 'Unassigned'}</div>
                  </div>
                  <span className={`px-2 py-1 text-[10px] rounded-full font-medium ${t.priority === 'P1' ? 'bg-destructive/10 text-destructive' : 'bg-orange-500/10 text-orange-600'}`}>
                    {t.priority === 'P1' ? 'Critical' : 'Overdue'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
